import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from '@mui/material'
import { PatientService } from '../../../../services/patient.service'
import {
  RPMEnrollment,
  RPMActivityType,
  RPM_ACTIVITY_DISPLAY,
  getTimeBillingTier,
  formatSeconds,
} from '../../../../model/rpm.types'
import { useCurrentUser } from '../../../../utils/use-current-user'

interface IProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  patientId?: number
  rpmEnrollment: RPMEnrollment | null
}

const patientService = new PatientService()

// Quick duration presets (in seconds)
const DURATION_PRESETS = [
  { label: '5m', seconds: 300 },
  { label: '10m', seconds: 600 },
  { label: '15m', seconds: 900 },
  { label: '20m', seconds: 1200 },
  { label: '30m', seconds: 1800 },
]

const RPMTimeEntryDialog: React.FC<IProps> = ({
  open,
  onClose,
  onSuccess,
  patientId,
  rpmEnrollment,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const { isProvider } = useCurrentUser()

  // Form state - now using minutes and seconds separately
  const [activityDate, setActivityDate] = useState('')
  const [minutes, setMinutes] = useState<number | ''>('')
  const [seconds, setSeconds] = useState<number | ''>(0)
  const [activityType, setActivityType] = useState<RPMActivityType>('data_review')
  const [notes, setNotes] = useState('')

  // Calculate total seconds
  const totalSeconds = (Number(minutes) || 0) * 60 + (Number(seconds) || 0)

  // Determine staff role from logged-in user
  const staffRole = isProvider ? 'provider' : 'navigator'

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Default to today
      const today = new Date().toISOString().split('T')[0]
      setActivityDate(today)
      setMinutes('')
      setSeconds(0)
      setActivityType('data_review')
      setNotes('')
    }
  }, [open])

  const handleSubmit = async () => {
    if (!patientId || !rpmEnrollment || !activityDate || totalSeconds < 1) return

    setSubmitting(true)
    try {
      // Send both duration_minutes (for backwards compat) and duration_seconds
      const payload = {
        rpm_enrollment_id: rpmEnrollment.id,
        activity_date: activityDate,
        duration_minutes: Math.ceil(totalSeconds / 60), // Backend expects this
        duration_seconds: totalSeconds,
        activity_type: activityType,
        staff_role: staffRole,
        notes: notes || undefined,
      }

      const res = await patientService.addRPMTimeEntry(patientId, payload)
      if (res?.data?.success) {
        onSuccess()
      } else {
        console.error('Failed to add time entry:', res?.data?.error)
      }
    } catch (err) {
      console.error('Failed to add time entry:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle preset selection
  const handlePresetClick = (presetSeconds: number) => {
    setMinutes(Math.floor(presetSeconds / 60))
    setSeconds(presetSeconds % 60)
  }

  // Calculate what tier this would put us at (using minutes for tier calculation)
  const totalMinutes = Math.floor(totalSeconds / 60)
  const tier = totalSeconds > 0 ? getTimeBillingTier(totalMinutes) : null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add Time Entry</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          {/* Activity Type */}
          <FormControl fullWidth size="small">
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value as RPMActivityType)}
              label="Activity Type"
            >
              {Object.entries(RPM_ACTIVITY_DISPLAY).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date */}
          <TextField
            label="Activity Date"
            type="date"
            size="small"
            value={activityDate}
            onChange={(e) => setActivityDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              '& input[type="date"]::-webkit-calendar-picker-indicator': {
                filter: 'var(--calendar-icon-filter, none)',
                cursor: 'pointer'
              }
            }}
          />

          {/* Duration - Quick presets */}
          <div>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Duration
            </Typography>
            <Stack direction="row" spacing={0.5} mb={1} flexWrap="wrap">
              {DURATION_PRESETS.map((preset) => (
                <Button
                  key={preset.seconds}
                  size="small"
                  variant={totalSeconds === preset.seconds ? 'contained' : 'outlined'}
                  onClick={() => handlePresetClick(preset.seconds)}
                  sx={{ minWidth: 40 }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                type="number"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value ? Number(e.target.value) : '')}
                placeholder="0"
                size="small"
                label="Minutes"
                inputProps={{ min: 0 }}
                sx={{ width: 100 }}
              />
              <Typography>:</Typography>
              <TextField
                type="number"
                value={seconds}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : 0
                  setSeconds(Math.min(59, Math.max(0, val)))
                }}
                placeholder="0"
                size="small"
                label="Seconds"
                inputProps={{ min: 0, max: 59 }}
                sx={{ width: 100 }}
              />
            </Stack>
            {totalSeconds > 0 && (
              <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                Total: {formatSeconds(totalSeconds)} ({totalMinutes} min billable)
              </Typography>
            )}
          </div>

          {/* Billing tier preview */}
          {tier && totalSeconds > 0 && (
            <Typography variant="caption" color={tier.codes.length > 0 ? 'success.main' : 'text.secondary'}>
              {tier.description}
              {tier.codes.length > 0 && ` (${tier.codes.join(', ')})`}
            </Typography>
          )}

          {/* Notes */}
          <TextField
            label="Notes (Optional)"
            size="small"
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Describe the activity..."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={!activityDate || totalSeconds < 1 || submitting}
        >
          {submitting ? <CircularProgress size={16} /> : 'Add Time'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RPMTimeEntryDialog
